import {stockSourceKey} from 'src/equipment/stock-source'

import type {PickupUnit} from './schemas'

// Stock counts are keyed by `StockGroup.key` (asset type and source offering).
export type PickupSelection = {
    unitIds: string[]
    stockCounts: Record<string, number>
}

export type StockGroup = {
    key: string
    rentalAssetTypeId: string
    rentalAssetTypeName: string
    thirdPartyRentalAssetOfferingId: string | null
    thirdPartyRentalCompanyName: string | null
    units: PickupUnit[]
}

export function stockGroups(units: PickupUnit[]): StockGroup[] {
    const groups = new Map<string, StockGroup>()
    for (const unit of units.filter((candidate) => candidate.isStock)) {
        const key = stockSourceKey(unit)
        const group = groups.get(key) ?? {
            key,
            rentalAssetTypeId: unit.rentalAssetTypeId,
            rentalAssetTypeName: unit.rentalAssetTypeName,
            thirdPartyRentalAssetOfferingId: unit.thirdPartyRentalAssetOfferingId,
            thirdPartyRentalCompanyName: unit.thirdPartyRentalCompanyName,
            units: [],
        }
        group.units.push(unit)
        groups.set(key, group)
    }
    return [...groups.values()]
}

export function stockGroupLabel(group: StockGroup): string {
    return group.thirdPartyRentalCompanyName
        ? `${group.rentalAssetTypeName} from ${group.thirdPartyRentalCompanyName}`
        : group.rentalAssetTypeName
}

export function stockLines(units: PickupUnit[], selection: PickupSelection) {
    return stockGroups(units).map((group) => ({
        rentalAssetTypeId: group.rentalAssetTypeId,
        thirdPartyRentalAssetOfferingId: group.thirdPartyRentalAssetOfferingId,
        quantity: selection.stockCounts[group.key] ?? 0,
    }))
}

export function selectAll(units: PickupUnit[]): PickupSelection {
    return {
        unitIds: units.filter((unit) => !unit.isStock).map((unit) => unit.rentalAssetId),
        stockCounts: Object.fromEntries(
            stockGroups(units).map((group) => [group.key, group.units.length]),
        ),
    }
}

export function selectedCount(selection: PickupSelection): number {
    return (
        selection.unitIds.length +
        Object.values(selection.stockCounts).reduce((total, count) => total + count, 0)
    )
}

/** Named unit ids plus the first N units of each stock group. */
export function selectedUnitIds(units: PickupUnit[], selection: PickupSelection): string[] {
    return [
        ...selection.unitIds,
        ...stockGroups(units).flatMap((group) =>
            group.units
                .slice(0, selection.stockCounts[group.key] ?? 0)
                .map((unit) => unit.rentalAssetId),
        ),
    ]
}

export function pickupUnitLabel(
    unit: Pick<
        PickupUnit,
        'displayName' | 'unitName' | 'rentalAssetTypeName' | 'thirdPartyRentalCompanyName'
    >,
): string {
    return unit.thirdPartyRentalCompanyName !== null || unit.unitName === null
        ? unit.displayName
        : `${unit.unitName} (${unit.rentalAssetTypeName})`
}

type OrderJob = {
    id: string
    name: string
    displayId: string
    status: 'planned' | 'active' | 'completed' | 'cancelled'
    startDate: string
    isPickup?: boolean
    dateTbc?: boolean
}

export function openPickups<T extends OrderJob>(jobs: T[]): T[] {
    return jobs.filter(
        (job) => job.isPickup && (job.status === 'planned' || job.status === 'active'),
    )
}
